import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Logo from '@/assets/images/LOGO/QODEARIS (3).svg';
import { Button, Drawer, Dropdown } from 'antd';
import { Menu, ChevronDown, Globe } from 'lucide-react';
import type { MenuProps } from 'antd';
import { useTranslation } from 'react-i18next';

export const Navbar: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const location = useLocation();

  const languageNames: { [key: string]: string } = {
    en: 'English',
    hi: 'हिंदी',
    mr: 'मराठी'
  };

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
  };

  const languageItems: MenuProps['items'] = [
    {
      key: 'en',
      label: 'English',
      onClick: () => changeLanguage('en'),
    },
    {
      key: 'hi',
      label: 'हिंदी',
      onClick: () => changeLanguage('hi'),
    },
    {
      key: 'mr',
      label: 'मराठी',
      onClick: () => changeLanguage('mr'),
    },
  ];

  const productMenuItems: MenuProps['items'] = [
    {
      key: 'dhartinetra',
      label: <Link to="/products/dhartinetra">{t('nav.dhartinetra')}</Link>,
    },
    {
      key: 'weather-station',
      label: <Link to="/products/weather-station">{t('nav.weatherStation')}</Link>,
    },
  ];

  const isActive = (path: string) => location.pathname === path;
  const isProductsActive = location.pathname.startsWith('/products');

  return (
    <header className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm shadow-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <img 
              src={Logo} 
              alt="QodeAris Logo" 
              className="h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8" aria-label="Main navigation">
            <Link
              to="/"
              className={`text-base font-medium transition-colors ${
                isActive('/')
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-gray-700 dark:text-gray-300 hover:text-green-600 dark:hover:text-green-400'
              }`}
              aria-current={isActive('/') ? 'page' : undefined}
            >
              {t('nav.home')}
            </Link>
            
            <Link
              to="/about"
              className={`text-base font-medium transition-colors ${
                isActive('/about')
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-gray-700 dark:text-gray-300 hover:text-green-600 dark:hover:text-green-400'
              }`}
              aria-current={isActive('/about') ? 'page' : undefined}
            >
              {t('nav.about')}
            </Link>

            <Dropdown menu={{ items: productMenuItems }} placement="bottomLeft">
              <button
                className={`text-base font-medium transition-colors flex items-center gap-1 ${
                  isProductsActive
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-gray-700 dark:text-gray-300 hover:text-green-600 dark:hover:text-green-400'
                }`}
                aria-label="Products menu"
              >
                {t('nav.products')}
                <ChevronDown className="w-4 h-4" aria-hidden="true" />
              </button>
            </Dropdown>

            <Link
              to="/blogs"
              className={`text-base font-medium transition-colors ${
                isActive('/blogs')
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-gray-700 dark:text-gray-300 hover:text-green-600 dark:hover:text-green-400'
              }`}
              aria-current={isActive('/blogs') ? 'page' : undefined}
            >
              {t('nav.blogs')}
            </Link>

            <Link
              to="/contact"
              className={`text-base font-medium transition-colors ${
                isActive('/contact')
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-gray-700 dark:text-gray-300 hover:text-green-600 dark:hover:text-green-400'
              }`}
              aria-current={isActive('/contact') ? 'page' : undefined}
            >
              {t('nav.contact')}
            </Link>
          </nav>

          {/* Desktop CTA Buttons */}
          <div className="hidden md:flex items-center space-x-3">
            <Dropdown menu={{ items: languageItems }} placement="bottomRight" arrow>
              <Button 
                type="text" 
                className="flex items-center gap-2 text-gray-700 dark:text-gray-300 hover:text-green-600 dark:hover:text-green-400"
                aria-label="Select Language"
              >
                <Globe className="w-4 h-4" />
                <span className="hidden lg:inline">{languageNames[i18n.language] || 'English'}</span>
                <ChevronDown className="w-3 h-3 opacity-50" />
              </Button>
            </Dropdown>

            <Link to="/login">
              <Button type="default" size="large">
                {t('nav.signIn')}
              </Button>
            </Link>
            <Link to="/dashboard">
              <Button type="primary" size="large" className="bg-green-600 hover:bg-green-700">
                {t('nav.dashboard')}
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Open mobile menu"
            aria-expanded={mobileMenuOpen}
          >
            <Menu className="w-6 h-6 text-gray-700 dark:text-gray-300" aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* Mobile Menu Drawer */}
      <Drawer
        title="Menu"
        placement="right"
        onClose={() => setMobileMenuOpen(false)}
        open={mobileMenuOpen}
        width={280}
      >
        <nav className="flex flex-col space-y-4" aria-label="Mobile navigation">
          <Link
            to="/"
            className={`text-lg font-medium py-2 px-4 rounded-lg transition-colors ${
              isActive('/')
                ? 'bg-green-50 text-green-600 dark:bg-green-900 dark:text-green-400'
                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
            onClick={() => setMobileMenuOpen(false)}
          >
            {t('nav.home')}
          </Link>

          <Link
            to="/about"
            className={`text-lg font-medium py-2 px-4 rounded-lg transition-colors ${
              isActive('/about')
                ? 'bg-green-50 text-green-600 dark:bg-green-900 dark:text-green-400'
                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
            onClick={() => setMobileMenuOpen(false)}
          >
            {t('nav.about')}
          </Link>

          <div className="pl-4">
            <div className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-2">{t('nav.products')}</div>
            <Link
              to="/products/dhartinetra"
              className="block text-base font-medium py-2 px-4 rounded-lg transition-colors text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('nav.dhartinetra')}
            </Link>
            <Link
              to="/products/weather-station"
              className="block text-base font-medium py-2 px-4 rounded-lg transition-colors text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('nav.weatherStation')}
            </Link>
          </div>

          <Link
            to="/blogs"
            className={`text-lg font-medium py-2 px-4 rounded-lg transition-colors ${
              isActive('/blogs')
                ? 'bg-green-50 text-green-600 dark:bg-green-900 dark:text-green-400'
                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
            onClick={() => setMobileMenuOpen(false)}
          >
            {t('nav.blogs')}
          </Link>

          <Link
            to="/contact"
            className={`text-lg font-medium py-2 px-4 rounded-lg transition-colors ${
              isActive('/contact')
                ? 'bg-green-50 text-green-600 dark:bg-green-900 dark:text-green-400'
                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
            onClick={() => setMobileMenuOpen(false)}
          >
            {t('nav.contact')}
          </Link>

          <div className="pt-4 border-t border-gray-200 dark:border-gray-700 space-y-3">
            <div className="px-4 py-2">
              <div className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-2">Language</div>
              <div className="flex flex-wrap gap-2">
                {languageItems?.map((item: any) => (
                  <Button 
                    key={item.key}
                    size="small"
                    type={i18n.language === item.key ? 'primary' : 'default'}
                    onClick={() => {
                      item.onClick();
                      setMobileMenuOpen(false);
                    }}
                    className={i18n.language === item.key ? 'bg-green-600' : ''}
                  >
                    {item.label}
                  </Button>
                ))}
              </div>
            </div>

            <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
              <Button type="default" size="large" block>
                {t('nav.signIn')}
              </Button>
            </Link>
            <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)}>
              <Button type="primary" size="large" block className="bg-green-600 hover:bg-green-700">
                {t('nav.dashboard')}
              </Button>
            </Link>
          </div>
        </nav>
      </Drawer>
    </header>
  );
};
